import { ARRAY_ERROR_KEYS, LTO_FORM_KEYS, OFFER_TARGET_TYPE } from "../constants";
import {
  TRIGGER_TIER_META,
  TRIGGER_TYPE,
  isTieredTrigger,
  isTimeProximityTrigger,
} from "./unifiedOffer";
import {
  validateTiers,
  validateVipWindows,
  validateBatchValues,
} from "../limitedOfferValidators";

/**
 * Form-level validation for the unified offer config.
 *
 * Array-level rules (≥1 row, ascending/descending, overlap, batch default) are
 * returned under `*_error` keys, NEVER under the array path itself. final-form's
 * processErrors writes every registered field via `setIn(errors, name, value)`;
 * a scalar at `tiers` while nested fields `tiers[0].x` exist throws
 * "Cannot set a numeric property on an object". A `tiers_error` key has no
 * nested siblings, so it is always safe and still blocks submit.
 *
 * Kept dependency-free (no React) so it can be unit-tested directly.
 */
export const validateUnifiedConfig = (values = {}) => {
  const errors = {};

  // Trigger config only exists once a target is chosen; skip array validation
  // while nothing is selected (Universal counts as always chosen).
  const isUniversal =
    values[LTO_FORM_KEYS.TARGET_TYPE] === OFFER_TARGET_TYPE.ALL_OFFERINGS;
  const hasOfferings = (values[LTO_FORM_KEYS.LISTINGS] || []).length > 0;
  const hasPaymentPages =
    (values[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS] || []).length > 0;
  if (!isUniversal && !hasOfferings && !hasPaymentPages) return errors;

  const trigger = values[LTO_FORM_KEYS.TRIGGER_TYPE] || TRIGGER_TYPE.SCHEDULE;

  if (isTieredTrigger(trigger)) {
    errors[ARRAY_ERROR_KEYS.TIERS] = validateTiers(values[LTO_FORM_KEYS.TIERS], {
      countdown: !!TRIGGER_TIER_META[trigger]?.countdown,
    });
  } else if (isTimeProximityTrigger(trigger)) {
    // Time tiers: both columns required, but no monotonic ordering.
    errors[ARRAY_ERROR_KEYS.TIERS] = validateTiers(
      values[LTO_FORM_KEYS.TIERS],
      { requireOrder: false }
    );
  } else if (trigger === TRIGGER_TYPE.VIP_SLOTS) {
    errors[ARRAY_ERROR_KEYS.VIP] = validateVipWindows(
      values[LTO_FORM_KEYS.VIP_WINDOWS]
    );
  } else if (trigger === TRIGGER_TYPE.BATCH_WISE) {
    errors[ARRAY_ERROR_KEYS.BATCH] = validateBatchValues(
      values[LTO_FORM_KEYS.BATCH_VALUES]
    );
  }

  Object.keys(errors).forEach((key) => {
    if (errors[key] === undefined) delete errors[key];
  });
  return errors;
};

